import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";

function Stars({ rating }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`text-lg ${n <= rating ? "text-amber-400" : "text-sensoria-border"}`}>★</span>
      ))}
    </span>
  );
}

export default function PlacePublic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        // Use a direct fetch since this may be a public (non-auth) page
        const res = await fetch(`/api/places/public/${slug}`);
        if (!res.ok) throw new Error("Not found");
        const json = await res.json();
        setData(json);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sensoria-bg">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-sensoria-bg px-4 text-center">
        <div className="mb-3 text-4xl">📍</div>
        <h1 className="font-display text-2xl font-bold text-white">Lieu introuvable</h1>
        <button onClick={() => navigate("/welcome")} className="sensoria-btn mt-4 px-6 py-2 text-sm">
          Rejoindre Univers Libertin
        </button>
      </div>
    );
  }

  const { place, reviews } = data;

  return (
    <div className="min-h-screen bg-sensoria-bg">
      {/* Ambient */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -left-40 -top-40 h-[400px] w-[400px] rounded-full bg-sensoria-accent/3 blur-[120px]" />
      </div>

      {/* Top bar */}
      <nav className="border-b border-sensoria-border/50 bg-sensoria-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <h1 className="font-display text-xl font-bold text-white">
            Sens<span className="text-sensoria-accent">oria</span>
          </h1>
          {user ? (
            <button onClick={() => navigate("/places")} className="sensoria-btn px-4 py-1.5 text-sm">
              Retour à l'annuaire
            </button>
          ) : (
            <button onClick={() => navigate("/welcome")} className="sensoria-btn px-4 py-1.5 text-sm">
              Créer un compte gratuit
            </button>
          )}
        </div>
      </nav>

      <main className="relative z-10 mx-auto max-w-3xl px-4 py-8">
        {/* Place header */}
        <div className="sensoria-card sensoria-glow">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-sensoria-accent/10 text-3xl">
              📍
            </div>
            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold text-white">{place.name}</h1>
              <p className="mt-1 text-sm text-sensoria-text-dim">
                {place.city}{place.department ? ` (${place.department})` : ""}
                {place.region ? ` · ${place.region}` : ""}
              </p>
              {place.avg_rating > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <Stars rating={Math.round(place.avg_rating)} />
                  <span className="text-sm text-sensoria-muted">
                    {Number(place.avg_rating).toFixed(1)} ({place.review_count} avis)
                  </span>
                </div>
              )}
            </div>
          </div>
          {place.description && (
            <p className="mt-4 text-sm leading-relaxed text-sensoria-text">{place.description}</p>
          )}
          {(place.features || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {place.features.map((f) => (
                <span key={f} className="rounded-full bg-sensoria-surface px-2 py-0.5 text-[10px] text-sensoria-text-dim">{f}</span>
              ))}
            </div>
          )}
        </div>

        {/* Public reviews (limited) */}
        {reviews.length > 0 && (
          <div className="mt-6 space-y-3">
            <h2 className="font-display text-lg font-semibold text-white">Avis de la communauté</h2>
            {reviews.map((r) => (
              <div key={r.id} className="sensoria-card">
                <div className="flex items-center gap-2">
                  <Stars rating={r.rating} />
                  <span className="text-xs text-sensoria-muted">
                    {new Date(r.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
                {r.comment && <p className="mt-2 text-sm text-sensoria-text-dim">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}

        {/* CTA wall */}
        <div className="mt-8 sensoria-card sensoria-glow text-center">
          <div className="mb-3 text-4xl">🔒</div>
          <h3 className="font-display text-xl font-bold text-white">
            Voir tous les avis, photos et détails
          </h3>
          <p className="mt-2 text-sm text-sensoria-text-dim">
            Créez un compte gratuit pour accéder à l'ensemble du contenu et contacter la communauté.
          </p>
          <button
            onClick={() => navigate("/welcome")}
            className="sensoria-btn mt-4 px-8 py-3"
          >
            Créer un compte gratuit
          </button>
          <p className="mt-3 text-xs text-sensoria-muted">
            Gratuit · Confidentiel · Modération IA
          </p>
        </div>
      </main>
    </div>
  );
}
